// /login/mfa/recovery — 6-digit code entry for MFA recovery.
//
// Shown immediately after the user clicks "I lost my authenticator" on the
// MFA challenge screen. The Custom Auth Flow emailed them a 6-digit code;
// they enter it here. On success the hook transitions to
// mfa-reenrollment-pending (QR + verify), handed off to MFASetupWizard.

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CodeInput,
  LangToggle,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function MFARecoveryCodeScreen() {
  const { t, lang, setLang } = useI18n();
  const { status, error, submitMfaRecoveryCode, clearError, signOut } = useAuth();

  const [code, setCode] = useState('');

  const submitting = status.kind === 'submitting-mfa-recovery-code';
  const emailHint =
    status.kind === 'mfa-recovery-code-sent' ? status.emailHint : undefined;

  // Clear the code when a wrong-code error fires (submitting →
  // mfa-recovery-code-sent transition with error.code === 'WRONG_CODE').
  // Same pattern as MFAChallengeScreen.
  const prevStatusKindRef = useRef(status.kind);
  useEffect(() => {
    if (
      prevStatusKindRef.current === 'submitting-mfa-recovery-code' &&
      status.kind === 'mfa-recovery-code-sent' &&
      error?.code === 'WRONG_CODE'
    ) {
      setCode('');
    }
    prevStatusKindRef.current = status.kind;
  }, [status.kind, error]);

  const onComplete = (full: string) => {
    if (full.length === 6 && !submitting) {
      void submitMfaRecoveryCode(full);
    }
  };

  // The body text names the hint-masked destination when the CreateAuth
  // Lambda returned one (and the SPA is configured to display it); falls
  // back to the generic "the email on file" phrasing otherwise. Enumeration
  // resistance is already enforced on the Lambda side — the hint only ever
  // comes back for accounts the user actually owns.
  const bodyTemplate = emailHint ? t('mfaRecoveryCode.bodyTemplate') : null;
  const bodyParts = bodyTemplate ? bodyTemplate.split('{email}') : null;

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
        <LangToggle value={lang} onChange={setLang} />
      </div>

      <div className={styles.formColumn}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{t('mfaRecoveryCode.eyebrow')}</span>
          <h1 className={styles.title}>{t('mfaRecoveryCode.headline')}</h1>
          <p className={styles.body}>
            {bodyParts ? (
              <>
                {bodyParts[0]}
                <strong className={styles.bodyEmphasis}>{emailHint}</strong>
                {bodyParts[1]}
              </>
            ) : (
              t('mfaRecoveryCode.bodyGeneric')
            )}
          </p>
        </header>

        <Card>
          <div className={styles.form}>
            {error && (
              <Alert severity="danger" onDismiss={clearError}>
                {error.message}
              </Alert>
            )}

            <div className={styles.codeInputCenter}>
              <CodeInput
                value={code}
                onChange={setCode}
                onComplete={onComplete}
                ariaLabel={t('mfaRecoveryCode.codeLabel')}
                disabled={submitting}
                errorMessage={error?.code === 'WRONG_CODE' ? error.message : null}
              />
            </div>

            <p className={styles.captionUnder}>
              {t('mfaRecoveryCode.expiresCaption')}
            </p>

            <Button
              kind="brand"
              fullWidth
              loading={submitting}
              disabled={code.length !== 6 || submitting}
              onClick={() => onComplete(code)}
            >
              {t('mfaRecoveryCode.submitButton')}
            </Button>
          </div>

          <div className={styles.fallbackStack}>
            <button
              type="button"
              className={styles.fallbackLink}
              onClick={() => void signOut()}
            >
              {t('mfaRecoveryCode.cancelLink')}
            </button>
          </div>
        </Card>
      </div>
    </AdminAuthShell>
  );
}
